import { useEffect, useRef, useState, type ReactNode } from 'react'

import { zahlLesen, zahlText } from '../format'
import { useAktion } from '../hooks'
import Meldungen from './Meldungen'

/**
 * Eine Stammdatenliste mit Anlegen und Bearbeiten: oben das Formular (wie bei den Artikeln - wer eine
 * Zeile ändert, will die Nachbarzeilen sehen), darunter die Tabelle. Die Rohstoffnotierungen haben
 * vier solche Listen (Spalten, Regeln, Lieferanten, Rohstoffe); die Felder beschreibt jede Seite als Daten.
 * Ein Feld kann von einem anderen abhängen (die Spalten zur Auswahl hängen am gewählten Lieferanten,
 * die Spalte gibt es nur bei „in Spalte übernehmen“) - dann bekommt es die Werte des Formulars.
 *
 * Zahlenfelder halten Text, bis gespeichert wird (Gestaltungsregel) - gelesen wird mit `zahlLesen`,
 * und ein Fehler nennt das Feld.
 */

type Wert = string | number | boolean | null | string[] | undefined

/** Der Stand des Formulars: je Feld der Text (oder bei Schaltern ja/nein), so wie er im Feld steht. */
export type Formularwerte = Record<string, string | boolean>

type Option = { wert: string; text: string }

export interface StammFeld<T> {
  name: Extract<keyof T, string>
  titel: string
  /** liste = Textliste, im Feld durch Komma oder Zeilenumbruch getrennt */
  art?: 'text' | 'zahl' | 'liste' | 'schalter' | 'auswahl'
  /** für `auswahl`: Werte als Text; `alsZahl` liest sie als Id zurück. Als Funktion: hängt am Formular. */
  optionen?: Option[] | ((werte: Formularwerte) => Option[])
  alsZahl?: boolean
  /** nur im Formular, wenn das gilt; sonst ausgeblendet, nicht Pflicht und als leer gespeichert */
  zeigenWenn?: (werte: Formularwerte) => boolean
  /** Schalter direkt in der Tabelle umlegen (speichert nur dieses Feld), nicht erst über „Bearbeiten“ */
  direkt?: boolean
  pflicht?: boolean
  platzhalter?: string
  /** breites Feld (Regel, Hinweis) - nimmt im Formular eine ganze Zeile */
  breit?: boolean
  /** in der Tabelle umbrechen statt die Zeile breit zu machen (schmaler als `breit`) */
  umbruch?: boolean
  /** eigene Darstellung in der Tabelle; sonst der Wert in Worten */
  zeigen?: (zeile: T) => ReactNode
  /** nur in der Tabelle, nicht im Formular (Zähler, abgeleitete Namen) */
  nurAnzeige?: boolean
  /** nur im Formular, nicht in der Tabelle (die Id hinter einem angezeigten Namen) */
  nurFormular?: boolean
}

interface Eigenschaften<T extends { id: number }> {
  /** wie eine Zeile heißt: „Spalte", „Lieferant" */
  einzahl: string
  zeilen: T[] | null
  felder: StammFeld<T>[]
  /** Gibt die Seite etwas zurück, steht es hinter der Erfolgsmeldung („… gespeichert. 2 Läufe neu bewertet …“). */
  speichern: (id: number | null, werte: Partial<T>) => Promise<ReactNode | void>
  loeschen?: (zeile: T) => Promise<unknown>
  /** die Rückfrage vor dem Löschen; sonst „… wirklich löschen?“ */
  loeschFrage?: (zeile: T) => string
  /** Vorgaben für eine neue Zeile */
  neu?: Partial<T>
  leer: string
  /** Satz über dem Formular */
  erklaerung?: string
  /**
   * Wohin „Bearbeiten" scrollt: `seite` an den Seitenanfang (die Liste ist die Seite), `formular` zum
   * Formular - für eine Liste mitten auf einer langen Seite (Einstellungen, 02.11.2026).
   */
  scrollen?: 'seite' | 'formular'
}

function alsText(feld: { art?: string }, wert: Wert): string | boolean {
  if (feld.art === 'schalter') return wert === true
  if (wert === null || wert === undefined) return ''
  if (feld.art === 'zahl' && typeof wert === 'number') return zahlText(wert)
  if (Array.isArray(wert)) return wert.join(', ')
  return String(wert)
}

function optionenVon<T>(feld: StammFeld<T>, werte: Formularwerte): Option[] {
  return typeof feld.optionen === 'function' ? feld.optionen(werte) : (feld.optionen ?? [])
}

function sichtbar<T>(feld: StammFeld<T>, werte: Formularwerte): boolean {
  return feld.zeigenWenn ? feld.zeigenWenn(werte) : true
}

function inWorten<T>(feld: StammFeld<T>, zeile: T): ReactNode {
  if (feld.zeigen) return feld.zeigen(zeile)
  const wert = zeile[feld.name] as Wert
  if (feld.art === 'schalter') return wert ? 'ja' : 'nein'
  if (feld.art === 'auswahl') return optionenVon(feld, {}).find((o) => o.wert === String(wert))?.text ?? ''
  if (feld.art === 'zahl' && typeof wert === 'number') return wert.toLocaleString('de-DE')
  if (Array.isArray(wert)) return wert.join(', ')
  return wert === null || wert === undefined ? '' : String(wert)
}

export default function Stammtabelle<T extends { id: number }>({ einzahl, zeilen, felder, speichern, loeschen, loeschFrage, neu, leer, erklaerung, scrollen = 'seite' }: Eigenschaften<T>) {
  const formularFelder = felder.filter((f) => !f.nurAnzeige)
  const tabellenFelder = felder.filter((f) => !f.nurFormular)
  const leerWerte = () => Object.fromEntries(formularFelder.map((f) => [f.name, alsText(f, (neu?.[f.name] as Wert) ?? (f.art === 'schalter' ? true : null))]))
  const [bearbeitet, setBearbeitet] = useState<number | null>(null)
  const [offen, setOffen] = useState(false)
  const [werte, setWerte] = useState<Formularwerte>(leerWerte)
  // Was die Seite nach dem Speichern dazu meldet; gilt nur, solange die Erfolgsmeldung dieser Aktion steht.
  const [meldung, setMeldung] = useState<ReactNode>(null)
  const { fehler, erfolg, arbeitet, setFehler, ausfuehren } = useAktion()
  const formularRef = useRef<HTMLFormElement>(null)
  // Erst nach dem Rendern steht das Formular da, zu dem gescrollt werden kann.
  const [scrollWunsch, setScrollWunsch] = useState(0)
  useEffect(() => {
    if (scrollWunsch > 0) formularRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [scrollWunsch])

  /** Speichern über die Seite; ihre Antwort kommt hinter die Erfolgsmeldung. */
  async function sichern(id: number | null, ergebnis: Partial<T>) {
    setMeldung((await speichern(id, ergebnis)) ?? null)
  }

  function oeffnen(zeile: T | null) {
    setBearbeitet(zeile?.id ?? null)
    setWerte(zeile ? Object.fromEntries(formularFelder.map((f) => [f.name, alsText(f, zeile[f.name] as Wert)])) : leerWerte())
    setOffen(true)
    setFehler(null)
    if (scrollen === 'formular') setScrollWunsch((n) => n + 1)
    else window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function absenden() {
    const ergebnis: Record<string, Wert> = {}
    const fehlerListe: string[] = []
    for (const f of formularFelder) {
      const roh = werte[f.name]
      if (!sichtbar(f, werte)) {
        ergebnis[f.name] = f.art === 'schalter' ? false : null
        continue
      }
      if (f.art === 'schalter') {
        ergebnis[f.name] = roh === true
        continue
      }
      const text = String(roh ?? '').trim()
      if (text === '' && f.pflicht) fehlerListe.push(`${f.titel} fehlt.`)
      if (f.art === 'zahl') {
        const zahl = text === '' ? null : zahlLesen(text)
        if (text !== '' && zahl === null) fehlerListe.push(`${f.titel}: „${text}“ ist keine Zahl.`)
        ergebnis[f.name] = zahl
      } else if (f.art === 'liste') {
        ergebnis[f.name] = text.split(/[,\n]/).map((t) => t.trim()).filter(Boolean)
      } else if (f.art === 'auswahl') {
        // Eine Wahl, die nach einer Änderung weiter oben nicht mehr zur Auswahl steht, zählt nicht.
        const gilt = optionenVon(f, werte).some((o) => o.wert === text)
        if (text !== '' && !gilt && f.pflicht) fehlerListe.push(`${f.titel}: bitte neu wählen.`)
        ergebnis[f.name] = text === '' || !gilt ? null : f.alsZahl ? Number(text) : text
      } else {
        ergebnis[f.name] = text === '' ? null : text
      }
    }
    if (fehlerListe.length > 0) {
      setFehler(fehlerListe.join(' '))
      return
    }
    void ausfuehren(async () => {
      await sichern(bearbeitet, ergebnis as Partial<T>)
      setOffen(false)
      setBearbeitet(null)
    }, bearbeitet === null ? `${einzahl} angelegt.` : `${einzahl} gespeichert.`)
  }

  function entfernen(zeile: T) {
    if (!loeschen || !window.confirm(loeschFrage?.(zeile) ?? `${einzahl} wirklich löschen?`)) return
    setMeldung(null)
    void ausfuehren(() => loeschen(zeile), `${einzahl} gelöscht.`)
  }

  function umlegen(zeile: T, feld: StammFeld<T>, an: boolean) {
    void ausfuehren(() => sichern(zeile.id, { [feld.name]: an } as Partial<T>), `${einzahl} ${an ? 'eingeschaltet' : 'ausgeschaltet'}.`)
  }

  return (
    <>
      <Meldungen
        fehler={fehler}
        erfolg={
          erfolg && (
            <>
              {erfolg} {meldung}
            </>
          )
        }
      />
      {!offen && (
        <div className="leiste">
          <button className="haupt" onClick={() => oeffnen(null)}>
            {einzahl} anlegen
          </button>
          {erklaerung && <span className="hinweis">{erklaerung}</span>}
        </div>
      )}
      {offen && (
        <form
          ref={formularRef}
          className="karte stamm-formular"
          onSubmit={(e) => {
            e.preventDefault()
            absenden()
          }}
        >
          <h3>{bearbeitet === null ? `${einzahl} anlegen` : `${einzahl} ändern`}</h3>
          <div className="stamm-felder">
            {formularFelder.map((f) => {
              if (!sichtbar(f, werte)) return null
              const wert = werte[f.name]
              const setzen = (neuerWert: string | boolean) => setWerte((alt) => ({ ...alt, [f.name]: neuerWert }))
              const beschriftung = (
                <span className="hinweis">
                  {f.titel}
                  {f.pflicht ? ' *' : ''}
                </span>
              )
              if (f.art === 'schalter') {
                return (
                  <label key={f.name} className="haekchen stamm-schalter">
                    <input type="checkbox" checked={wert === true} onChange={(e) => setzen(e.target.checked)} /> {f.titel}
                  </label>
                )
              }
              if (f.art === 'auswahl') {
                return (
                  <label key={f.name} className="feld">
                    {beschriftung}
                    <select value={String(wert ?? '')} onChange={(e) => setzen(e.target.value)}>
                      <option value="">- bitte wählen -</option>
                      {optionenVon(f, werte).map((o) => (
                        <option key={o.wert} value={o.wert}>
                          {o.text}
                        </option>
                      ))}
                    </select>
                  </label>
                )
              }
              return (
                <label key={f.name} className={`feld${f.breit ? ' breit' : ''}`}>
                  {beschriftung}
                  <input
                    type="text"
                    inputMode={f.art === 'zahl' ? 'decimal' : undefined}
                    value={String(wert ?? '')}
                    placeholder={f.platzhalter}
                    onChange={(e) => setzen(e.target.value)}
                  />
                </label>
              )
            })}
          </div>
          <div className="leiste" style={{ marginBottom: 0 }}>
            <button className="haupt" type="submit" disabled={arbeitet}>
              Speichern
            </button>
            <button type="button" onClick={() => setOffen(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
      <div className="tabellenrahmen stammdaten-rahmen">
        <table className="stamm-liste">
          <thead>
            <tr>
              {tabellenFelder.map((f) => (
                <th key={`${f.name}-${f.titel}`} className={f.art === 'zahl' ? 'zahl' : undefined}>
                  {f.titel}
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(zeilen ?? []).map((z) => (
              <tr key={z.id} className={(z as { aktiv?: boolean }).aktiv === false ? 'leise' : undefined} onDoubleClick={() => oeffnen(z)}>
                {tabellenFelder.map((f) => (
                  <td key={`${f.name}-${f.titel}`} className={f.art === 'zahl' ? 'zahl' : f.breit ? 'stamm-breit' : f.umbruch ? 'stamm-umbruch' : undefined}>
                    {f.direkt && f.art === 'schalter' ? (
                      <label className="haekchen" title={`${einzahl} ${z[f.name] ? 'ausschalten' : 'einschalten'}`}>
                        <input type="checkbox" checked={z[f.name] === true} disabled={arbeitet} onChange={(e) => umlegen(z, f, e.target.checked)} />{' '}
                        {z[f.name] ? 'ja' : 'nein'}
                      </label>
                    ) : (
                      inWorten(f, z)
                    )}
                  </td>
                ))}
                <td className="stamm-knoepfe">
                  <button onClick={() => oeffnen(z)}>Bearbeiten</button>
                  {loeschen && (
                    <button className="leise" onClick={() => entfernen(z)}>
                      Löschen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {zeilen !== null && zeilen.length === 0 && <p className="leer">{leer}</p>}
      </div>
    </>
  )
}
